import React from "react";
import { Button } from "@/components/ui/button";
import { FolderSearch, LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

interface EmptyStateProps {
  icon?: LucideIcon;
  illustration?: React.ReactNode;
  title?: string;
  description?: string;
  actionLabel?: string;
  onAction?: () => void;
  className?: string;
  compact?: boolean;
}

export function EmptyState({
  // CHANGED: 'FolderSearch' is friendlier and more professional than 'FileX2'
  icon: Icon = FolderSearch,
  illustration,
  title = "No data found",
  description = "It looks empty here. There is no data to display at this time.",
  actionLabel,
  onAction,
  className,
  compact = false,
}: EmptyStateProps) {
  return (
    <div
      className={cn(
        "flex h-full w-full flex-col items-center justify-center rounded-md border border-dashed p-8 text-center animate-in fade-in-50",
        compact ? "p-4 min-h-[200px]" : "min-h-[400px] p-12",
        className
      )}
    >
      <div
        className={cn(
          "relative flex items-center justify-center",
          compact ? "mb-4" : "mb-8"
        )}
      >
        {illustration ? (
          illustration
        ) : (
          <>
            {/* Outer Ring: Soft Primary Fade */}
            <div
              className={cn(
                "absolute rounded-full bg-primary/5",
                compact ? "h-16 w-16" : "h-24 w-24"
              )}
            />

            {/* Inner Ring: Slightly sharper for depth */}
            <div
              className={cn(
                "absolute rounded-full bg-primary/10",
                compact ? "h-12 w-12" : "h-16 w-16"
              )}
            />

            {/* Icon Container: Added gradient and shadow for "Superior" look */}
            <div
              className={cn(
                "relative flex items-center justify-center rounded-2xl shadow-sm ring-1 ring-border bg-gradient-to-b from-background to-muted/50",
                compact ? "h-15 w-15 rounded-lg" : "h-14 w-14"
              )}
            >
              <Icon
                className={cn(
                  "text-muted-foreground/80", // Slightly darker for contrast
                  compact ? "h-5 w-5" : "h-7 w-7"
                )}
                strokeWidth={1.5} // Thinner lines look more modern/elegant
              />
            </div>
          </>
        )}
      </div>

      <div className="max-w-[420px] space-y-2 text-center">
        {/* Use <p> so global `h3` typography (2.4rem) does not override Tailwind sizes. */}
        <p
          className={cn(
            "font-medium tracking-tight text-foreground",
            compact ? "text-sm" : "text-lg"
          )}
        >
          {title}
        </p>
        {description && (
          <p
            className={cn(
              "text-muted-foreground",
              compact ? "text-xs" : "text-sm"
            )}
          >
            {description}
          </p>
        )}
      </div>

      {onAction && actionLabel && (
        <Button
          onClick={onAction}
          variant={compact ? "outline" : "default"}
          size={compact ? "sm" : "default"}
          className={cn("mt-6", compact && "mt-4")}
        >
          {actionLabel}
        </Button>
      )}
    </div>
  );
}
